import { noop } from 'rxjs';
import { I4BCellType, I4BColumnConfiguration, I4BGridFilterType } from './configurations';
import { getDefaultColumnsByConcept } from './default-columns.registry';
import {
  DaliaDevicesConfigurationButtonColumn,
  DaliaFirmwareVersionColumn,
  I4BBasicColumn,
  I4BColumn,
  I4BSelectionColumn,
  IdColumn,
  NameColumn
} from './definitions';
import { I4bCellConcept } from './enums';
import { DevicesConfigurationGridData, HeaderType, I4BColumnHeader, I4BGrid, I4BGridOptions } from './models';
import { I4BColumnOptions } from './options';
import { DeviceConfigurationStatus } from '@iot-platform/models/dalia';

export const GET_DEFAULT_DEVICES_CONFIGURATION_COLUMNS_FOR_DALIA_DEVICES_CONFIGURATION_VIEW = (): I4BColumn<
  I4BColumnHeader,
  I4BColumnConfiguration,
  I4BColumnOptions
>[] => [
  new IdColumn(
    { displayName: 'IDENTIFIER' },
    {
      id: 'identifier',
      concept: I4bCellConcept.DEVICES,
      width: 180,
      cell: { type: I4BCellType.BASIC_LINK, event: { type: 'navigateToDevice' } },
      isDefault: true,
      filterParams: {
        enabled: true
      }
    },
    { order: 1 }
  ),
  new NameColumn(
    { displayName: 'NAME', type: HeaderType.ICON_AND_TEXT, icon: 'device' },
    {
      id: 'name',
      translationKey: 'MASTER_VIEW_ENGINE.TABLE.HEADER_NAME.DEVICE_NAME',
      concept: I4bCellConcept.DEVICES,
      autoSize: true,
      isDefault: true,
      cell: { type: I4BCellType.BASIC_LINK, event: { type: 'navigateToDevice' } },
      filterParams: {
        enabled: true
      }
    },
    { order: 2, pinned: 'left', width: 200 }
  ),
  new I4BBasicColumn(
    { displayName: 'MODEL' },
    {
      id: 'model',
      translationKey: 'MASTER_VIEW_ENGINE.TABLE.HEADER_NAME.DEVICE_TYPE_MODEL',
      cell: { type: I4BCellType.BASIC },
      isDefault: true,
      sortProperty: 'model',
      concept: I4bCellConcept.DEVICES,
      width: 150,
      rowGroupParams: {
        isConfigurable: false,
        enabled: true,
        rowGroup: false,
        hiddenWhenGrouping: true
      },
      filterParams: {
        enabled: true,
        filterType: I4BGridFilterType.SET_COLUMN_FILTER
      }
    },
    {
      order: 3
    }
  ),
  new DaliaFirmwareVersionColumn(
    { displayName: 'FIRMWARE' },
    {
      id: 'currentFirmware.version',
      translationKey: 'MASTER_VIEW_ENGINE.TABLE.HEADER_NAME.DEVICE_TYPE_FIRMWARE',
      cell: {
        type: I4BCellType.BASIC,
        options: {
          toFixed: 3
        }
      },
      isDefault: true,
      sortProperty: 'currentFirmware.version',
      concept: I4bCellConcept.DEVICES,
      width: 160,
      filterParams: {
        enabled: true,
        filterType: I4BGridFilterType.SET_COLUMN_FILTER
      },
      rowGroupParams: {
        isConfigurable: false,
        enabled: true,
        rowGroup: false,
        hiddenWhenGrouping: true
      }
    },
    {
      order: 4
    }
  ),
  new I4BBasicColumn(
    { displayName: 'AFFILIATE ID' },
    {
      id: 'affiliateId',
      translationKey: 'MASTER_VIEW_ENGINE.TABLE.HEADER_NAME.AFFILIATE',
      cell: { type: I4BCellType.BASIC },
      isDefault: true,
      concept: I4bCellConcept.DEVICES,
      width: 160,
      sortProperty: 'affiliateId',
      rowGroupParams: {
        isConfigurable: false,
        enabled: true,
        rowGroup: false,
        hiddenWhenGrouping: true
      },
      filterParams: {
        enabled: true,
        filterType: I4BGridFilterType.SET_COLUMN_FILTER
      }
    },
    {
      order: 5
    }
  ),
  new I4BBasicColumn(
    { displayName: 'OPERATOR' },
    {
      id: 'operator',
      translationKey: 'MASTER_VIEW_ENGINE.TABLE.HEADER_NAME.OPERATOR',
      cell: { type: I4BCellType.BASIC },
      isDefault: true,
      concept: I4bCellConcept.DEVICES,
      width: 160,
      sortProperty: 'operator',
      rowGroupParams: {
        isConfigurable: false,
        enabled: true,
        rowGroup: false,
        hiddenWhenGrouping: true
      },
      filterParams: {
        enabled: true,
        filterType: I4BGridFilterType.SET_COLUMN_FILTER
      }
    },
    {
      order: 6
    }
  ),
  new I4BBasicColumn(
    { displayName: 'STATUS' },
    {
      id: 'configurationStatus',
      translationKey: 'MASTER_VIEW_ENGINE.TABLE.HEADER_NAME.STATUS',
      isDefault: true,
      cell: {
        type: I4BCellType.BASIC,
        cellClassRules: {
          'bg-orange100': `data.configurationStatus === "${DeviceConfigurationStatus.PENDING}"`,
          'bg-blue100': `data.configurationStatus === "${DeviceConfigurationStatus.PUBLISHED}"`,
          'bg-red100': `data.configurationStatus === "${DeviceConfigurationStatus.ERROR}"`,
          'text-center': 'true',
          'skip-line-through': 'true',
          capitalize: 'true'
        }
      },
      concept: I4bCellConcept.DEVICES,
      width: 140,
      sortProperty: 'configurationStatus',
      rowGroupParams: {
        isConfigurable: false,
        enabled: true,
        rowGroup: false,
        hiddenWhenGrouping: true
      },
      filterParams: {
        enabled: true,
        filterType: I4BGridFilterType.SET_COLUMN_FILTER
      }
    },
    {
      order: 7
    }
  ),
  new I4BBasicColumn(
    { displayName: 'CONFIGURATION DATE', type: HeaderType.BASIC },
    {
      translationKey: 'MASTER_VIEW_ENGINE.TABLE.HEADER_NAME.CONFIG_DATE',
      id: 'configurationAt',
      isDefault: true,
      width: 210,
      sortProperty: 'configurationAt',
      concept: I4bCellConcept.DEVICES,
      cell: {
        type: I4BCellType.DATE
      },
      filterParams: {
        enabled: true,
        filterType: I4BGridFilterType.DATE_COLUMN_FILTER
      }
    },
    {
      order: 8
    }
  ),
  new I4BBasicColumn(
    { displayName: 'NUMBER OF DOWNLOADS', type: HeaderType.BASIC },
    {
      translationKey: 'MASTER_VIEW_ENGINE.TABLE.HEADER_NAME.NUMBER_OF_DOWNLOADS',
      id: 'targetConfigurationDownloadsCount',
      isDefault: true,
      sortProperty: 'targetConfigurationDownloadsCount',
      width: 230,
      concept: I4bCellConcept.DEVICES,
      cell: {
        type: I4BCellType.NUMBER
      },
      filterParams: {
        enabled: true,
        filterType: I4BGridFilterType.NUMBER_COLUMN_FILTER
      }
    },
    {
      order: 9
    }
  )
];

export class DefaultDaliaDevicesConfigurationGrid implements I4BGrid<I4BGridOptions, DevicesConfigurationGridData> {
  masterview = 'dalia-devices-configuration';
  isDefault = false;
  isUserDefault = false;
  isAppDefault = false;
  name = null;
  userId = null;
  data: DevicesConfigurationGridData = {
    response: {
      data: [],
      pagination: null
    }
  };
  gridOptions: I4BGridOptions = {
    pageSize: -1,
    multipleSelection: false,
    autoRefresh: { enabled: true, delay: 120 },
    gridSort: [{ colId: 'name', sort: 'asc', sortIndex: 2 }],
    buttonColumn: { enabled: true, className: new DaliaDevicesConfigurationButtonColumn() },
    selectionColumn: { enabled: false, className: new I4BSelectionColumn() }
  };
  columns: I4BColumn<I4BColumnHeader, I4BColumnConfiguration, I4BColumnOptions>[] = [...getDefaultColumnsByConcept(this.masterview)];

  export = () => noop();
}
